// Modale "Tarifs du client" — affichage / édition de la grille tarifaire
// pour un contact donné.
// - Si le contact n'a pas de personnalisation : affiche le tarif standard
//   (is_custom = false). Toute modification le transforme en "personnalisé".
// - Si déjà personnalisé : affiche la version contact (is_custom = true).
// - Bouton "Réinitialiser au standard" si personnalisation existante.

import { useEffect, useState } from 'react';
import { X, Save, Loader2, RotateCcw, Tag, Star } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getTarifsForContact, saveTarifsForContact, resetTarifsForContact,
  getTarifsStandard,
} from '../services/api';
import { usePerm } from '../lib/perms';
import TarifsGrid from './TarifsGrid';
import TarifReadOnlyGrid from './TarifReadOnlyGrid';

export default function ContactTarifsModal({ contactId, contactName, onClose, forceReadonly = false }) {
  const [data, setData] = useState(null);
  // Reference standard (always the global one) — used to highlight overridden
  // fields on the contact's grid.
  const [standardData, setStandardData] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const userCanEdit = usePerm('tarifs_beta.edit');
  // When forceReadonly is true (mode "Voir le tarif de base"), we disable
  // editing regardless of the user permission.
  const canEdit = !forceReadonly && userCanEdit;

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    Promise.all([
      getTarifsForContact(contactId),
      getTarifsStandard().catch(() => null),
    ])
      .then(([r, std]) => { setData(r.data); setIsCustom(r.isCustom); setStandardData(std || null); setDirty(false); })
      .catch(() => toast.error('Erreur de chargement du tarif'))
      .finally(() => setLoading(false));
  }, [contactId]);

  const handleChange = (next) => {
    setData(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await saveTarifsForContact(contactId, data);
      setData(r.data);
      setIsCustom(r.isCustom);
      setDirty(false);
      toast.success('Tarifs du client enregistrés (personnalisé)');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const resetToStandard = async () => {
    if (!window.confirm('Retirer la personnalisation et revenir au tarif standard ?')) return;
    setResetting(true);
    try {
      const r = await resetTarifsForContact(contactId);
      setData(r.data);
      setIsCustom(r.isCustom);
      setDirty(false);
      toast.success('Tarifs réinitialisés (retour au standard)');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  const handleBackdropClick = () => {
    if (dirty && !window.confirm('Modifications non enregistrées. Fermer quand même ?')) return;
    onClose();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 14,
          width: 'min(960px, 100%)', maxHeight: '92vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
            <span style={{
              width: 32, height: 32, borderRadius: 8,
              background: isCustom ? '#fef3c7' : '#eef2ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {isCustom ? <Star size={16} color="#b45309" fill="#fbbf24" /> : <Tag size={16} color="#4f46e5" />}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Tarifs du client{contactName ? ` — ${contactName}` : ''}
              </h3>
              <div style={{ fontSize: 12, marginTop: 1 }}>
                {isCustom ? (
                  <span style={{ color: '#b45309', fontWeight: 600 }}>★ Tarif personnalisé</span>
                ) : (
                  <span style={{ color: '#64748b' }}>Tarif standard hérité — toute modification créera une personnalisation</span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleBackdropClick}
            style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#475569' }}
            title="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', background: '#f8fafc' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
              <Loader2 size={24} className="spin" style={{ color: '#6366f1' }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 0.9s linear infinite; }`}</style>
            </div>
          ) : data ? (
            forceReadonly ? (
              // En mode "Voir le tarif de base" : affichage en grille HTML (table)
              // au lieu des inputs editables. Pas de compareWith en mode base seul.
              <TarifReadOnlyGrid data={data} reference={standardData} />
            ) : (
              <TarifsGrid data={data} onChange={handleChange} readonly={!canEdit} compareWith={standardData} />
            )
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Aucun tarif disponible.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 20px', borderTop: '1px solid #e2e8f0', gap: 8,
        }}>
          <div>
            {isCustom && canEdit && (
              <button
                type="button"
                onClick={resetToStandard}
                disabled={resetting || saving}
                data-perm="antennes:tarifs_beta.edit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c',
                  fontSize: 12, cursor: 'pointer',
                }}
              >
                {resetting ? <Loader2 size={13} className="spin" /> : <RotateCcw size={13} />}
                Réinitialiser au tarif standard
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={handleBackdropClick}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid #e2e8f0', background: 'white', color: '#475569',
                fontSize: 13, cursor: 'pointer',
              }}
            >
              {canEdit ? 'Annuler' : 'Fermer'}
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={save}
                disabled={saving || !dirty}
                data-perm="antennes:tarifs_beta.edit"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 8,
                  border: 'none', background: '#4f46e5', color: 'white',
                  fontSize: 13, fontWeight: 600,
                  cursor: saving || !dirty ? 'not-allowed' : 'pointer',
                  opacity: saving || !dirty ? 0.6 : 1,
                }}
              >
                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                {isCustom ? 'Enregistrer' : 'Personnaliser'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
